import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/es';
import { BellIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import { useNotifications } from '../hooks/useNotifications';

moment.locale('es');

export default function NotificationBell() {
  const { notifications, actionRequired, displayCount, markAttended, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (!next) markAllRead();
  };

  const handleClick = (n) => {
    markAttended(n.id);
    setOpen(false);
    if (n.route) navigate(n.route);
  };

  const urgent = actionRequired.length > 0;
  const Icon = displayCount > 0 ? BellAlertIcon : BellIcon;
  const pendingNotifications = notifications.filter((n) => !n.read && !n.attendedAt);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        className="relative rounded-full p-2 text-gray-500 hover:bg-gray-100"
        aria-label="Notificaciones"
      >
        <Icon className={`h-6 w-6 ${urgent ? 'text-red-500' : displayCount > 0 ? 'text-orange-500' : 'text-gray-500'}`} />
        {displayCount > 0 && (
          <span className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white ${urgent ? 'bg-red-500' : 'bg-orange-500'}`}>
            {displayCount > 9 ? '9+' : displayCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 max-h-96 overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <span className="text-sm font-semibold text-gray-800">Notificaciones</span>
          </div>
          {pendingNotifications.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-gray-400">Sin notificaciones.</p>
          ) : (
            <ul className="divide-y divide-gray-50">
              {pendingNotifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleClick(n)}
                    className={`block w-full px-4 py-3 text-left text-sm hover:bg-gray-50 ${!n.read ? 'bg-sky-50/60' : ''}`}
                  >
                    <p className="font-medium text-gray-800">{n.message}</p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {n.createdAt?.toDate ? moment(n.createdAt.toDate()).fromNow() : ''}
                      {n.requiresAction && !n.read && (
                        <span className="ml-2 rounded-full bg-red-100 px-1.5 py-0.5 font-semibold text-red-600">Requiere acción</span>
                      )}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
